import { useState, useEffect, useCallback } from "react";
import { brandServicesService } from "../../../shared/services/apis";
import type {
  BrandService,
  BrandServiceQueryParams,
  CreateBrandServiceData,
  UpdateBrandServiceData,
  AssignServiceToBrandData,
  Service,
} from "../../../shared/types";

interface UseBrandServicesManagementReturn {
  brandServices: BrandService[];
  availableServices: Service[];
  loading: boolean;
  error: string | null;
  creating: boolean;
  updating: boolean;
  deleting: boolean;
  fetchBrandServices: (params?: BrandServiceQueryParams) => Promise<void>;
  fetchAvailableServices: (brandId: string) => Promise<void>;
  createBrandService: (data: CreateBrandServiceData) => Promise<void>;
  updateBrandService: (id: string, data: UpdateBrandServiceData) => Promise<void>;
  assignServiceToBrand: (data: AssignServiceToBrandData) => Promise<void>;
  unassignServiceFromBrand: (brandId: string, serviceId: string) => Promise<void>;
  toggleBrandServiceStatus: (id: string) => Promise<void>;
  deleteBrandService: (id: string) => Promise<void>;
  refreshBrandServices: () => Promise<void>;
}

export const useBrandServicesManagement = (): UseBrandServicesManagementReturn => {
  const [brandServices, setBrandServices] = useState<BrandService[]>([]);
  const [availableServices, setAvailableServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchBrandServices = useCallback(async (params?: BrandServiceQueryParams) => {
    try {
      setLoading(true);
      setError(null);
      const data = await brandServicesService.getBrandServices(params);
      setBrandServices(data);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to fetch brand services";
      setError(errorMessage);
      console.error("Error fetching brand services:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAvailableServices = useCallback(async (brandId: string) => {
    try {
      setError(null);
      const data = await brandServicesService.getAvailableServicesForBrand(brandId);
      setAvailableServices(data);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to fetch available services";
      setError(errorMessage);
      console.error("Error fetching available services:", err);
    }
  }, []);

  const createBrandService = useCallback(async (data: CreateBrandServiceData) => {
    try {
      setCreating(true);
      setError(null);
      const newBrandService = await brandServicesService.createBrandService(data);
      setBrandServices(prev => [newBrandService, ...prev]);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to create brand service";
      setError(errorMessage);
      console.error("Error creating brand service:", err);
      throw err;
    } finally {
      setCreating(false);
    }
  }, []);

  const updateBrandService = useCallback(async (id: string, data: UpdateBrandServiceData) => {
    try {
      setUpdating(true);
      setError(null);
      const updatedBrandService = await brandServicesService.updateBrandService(id, data);
      setBrandServices(prev =>
        prev.map(brandService => (brandService.id === id ? updatedBrandService : brandService))
      );
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to update brand service";
      setError(errorMessage);
      console.error("Error updating brand service:", err);
      throw err;
    } finally {
      setUpdating(false);
    }
  }, []);

  const assignServiceToBrand = useCallback(async (data: AssignServiceToBrandData) => {
    try {
      setCreating(true);
      setError(null);
      const newBrandService = await brandServicesService.assignServiceToBrand(data);
      setBrandServices(prev => [newBrandService, ...prev]);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to assign service to brand";
      setError(errorMessage);
      console.error("Error assigning service to brand:", err);
      throw err;
    } finally {
      setCreating(false);
    }
  }, []);

  const unassignServiceFromBrand = useCallback(async (brandId: string, serviceId: string) => {
    try {
      setDeleting(true);
      setError(null);
      await brandServicesService.unassignServiceFromBrand(brandId, serviceId);
      setBrandServices(prev => 
        prev.filter(brandService => 
          !(brandService.brandId === brandId && brandService.serviceId === serviceId)
        )
      );
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to unassign service from brand";
      setError(errorMessage);
      console.error("Error unassigning service from brand:", err);
      throw err;
    } finally {
      setDeleting(false);
    }
  }, []);

  const toggleBrandServiceStatus = useCallback(async (id: string) => {
    try {
      setUpdating(true);
      setError(null);
      const updatedBrandService = await brandServicesService.toggleBrandServiceStatus(id);
      setBrandServices(prev =>
        prev.map(brandService => (brandService.id === id ? updatedBrandService : brandService))
      );
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to toggle brand service status";
      setError(errorMessage);
      console.error("Error toggling brand service status:", err);
      throw err;
    } finally {
      setUpdating(false);
    }
  }, []);

  const deleteBrandService = useCallback(async (id: string) => {
    try {
      setDeleting(true);
      setError(null);
      await brandServicesService.deleteBrandService(id);
      setBrandServices(prev => prev.filter(brandService => brandService.id !== id));
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to delete brand service";
      setError(errorMessage);
      console.error("Error deleting brand service:", err);
      throw err;
    } finally {
      setDeleting(false);
    }
  }, []);

  const refreshBrandServices = useCallback(() => {
    return fetchBrandServices();
  }, [fetchBrandServices]);

  useEffect(() => {
    fetchBrandServices();
  }, [fetchBrandServices]);

  return {
    brandServices,
    availableServices,
    loading,
    error,
    creating,
    updating,
    deleting,
    fetchBrandServices,
    fetchAvailableServices,
    createBrandService,
    updateBrandService,
    assignServiceToBrand,
    unassignServiceFromBrand,
    toggleBrandServiceStatus,
    deleteBrandService,
    refreshBrandServices,
  };
};